import { Upload, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useUIStore } from '@/stores';
import { cn } from '@/lib/utils';

export function UploadIndicator() {
  const { globalUploads } = useUIStore();

  const activeUploads = globalUploads.filter(
    (u) => u.status === 'uploading' || u.status === 'processing'
  );

  if (activeUploads.length === 0) return null;

  const totalProgress = activeUploads.reduce((sum, u) => sum + u.progress, 0) / activeUploads.length;

  return (
    <div className="mx-3 mb-3">
      <div className="bg-background-tertiary border border-border-subtle rounded-xl p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent-primary/10 flex items-center justify-center">
            <Loader2 size={16} className="text-accent-primary animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              Uploading {activeUploads.length} file{activeUploads.length > 1 ? 's' : ''}
            </div>
            <div className="text-xs text-foreground-muted">
              {Math.round(totalProgress)}% complete
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-background-hover rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-accent-primary to-accent-secondary transition-all duration-300 rounded-full"
            style={{ width: `${totalProgress}%` }}
          />
        </div>

        {/* Individual files */}
        <div className="space-y-1 max-h-24 overflow-y-auto">
          {activeUploads.map((upload) => (
            <div
              key={upload.id}
              className="flex items-center gap-2 text-xs text-foreground-muted"
            >
              <Upload size={12} className="flex-shrink-0" />
              <span className="truncate flex-1">{upload.fileName}</span>
              <span className="flex-shrink-0">{upload.progress}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function UploadStatusToast() {
  const { globalUploads, removeGlobalUpload } = useUIStore();

  const completedUploads = globalUploads.filter(
    (u) => u.status === 'success' || u.status === 'error'
  );

  if (completedUploads.length === 0) return null;

  return (
    <div className="mx-3 mb-3 space-y-2">
      {completedUploads.slice(0, 3).map((upload) => (
        <div
          key={upload.id}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all',
            upload.status === 'success'
              ? 'bg-green-500/10 border border-green-500/30'
              : 'bg-red-500/10 border border-red-500/30'
          )}
        >
          {upload.status === 'success' ? (
            <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
          ) : (
            <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
          )}
          <span className="truncate flex-1 text-foreground-secondary">
            {upload.fileName}
          </span>
          <button
            onClick={() => removeGlobalUpload(upload.id)}
            className="p-0.5 hover:bg-background-hover rounded transition-colors"
          >
            <X size={12} className="text-foreground-muted" />
          </button>
        </div>
      ))}
    </div>
  );
}
